<!--
  Vault.vue — Vault Video storefront (Vue 3, <script setup>).
  A dedicated page for /shop/vault: same purchase/membership/analytics
  machinery as StarShop/Index.vue (via useStarShopPurchase), but its own
  vault-branded hero copy and one row per category instead of one row per
  mechanic.
-->
<script setup>
import { computed, nextTick, onMounted, ref } from 'vue'
import { usePage } from '@inertiajs/vue3'
import HeaderV2 from '@/Components/Reels/HeaderV2.vue'
import BuyStarsModal from '@/Components/Reels/BuyStarsModal.vue'
import MembershipUpsellModal from '@/Components/StarShop/MembershipUpsellModal.vue'
import InterstitialAdModal from '@/Components/StarShop/InterstitialAdModal.vue'
import StarIcon from './StarIcon.vue'
import StarMarkSVG from './StarMarkSVG.vue'
import CelebrationBurst from './CelebrationBurst.vue'
import RowShell from './RowShell.vue'
import DirectAccessProductCard from './DirectAccessProductCard.vue'
import ShopAllModal from './ShopAllModal.vue'
import { getCategoryColor } from './vaultCategoryColors.js'
import { useStarShopPurchase } from '@/composables/useStarShopPurchase'

const CARDS_PER_ROW = 4

const props = defineProps({
    items:              { type: Array,  default: () => [] },
    categoryOrder:      { type: Array,  default: () => [] },
    starPacks:          { type: Array,  default: () => [] },
    highlightProductId: { type: Number, default: null },
    shopUrl:            { type: String, default: '' },
    analyticsContext:   { type: Object, default: () => ({}) },
})

const page = usePage()
const isGoldUser = computed(() => page.props.auth?.is_gold ?? false)
const requiresAgeVerification = computed(() => page.props.requiresAgeVerification ?? false)

const availableItems = computed(() => props.items.filter(o => o.available > 0 || o.unlocked))

const mechanicByProductId = computed(() => {
    const map = {}
    for (const o of availableItems.value) map[o.product_id] = 'vault_video'
    return map
})
const productNameById = computed(() => {
    const map = {}
    for (const o of availableItems.value) map[o.product_id] = o.product_name
    return map
})
const performerNameById = computed(() => {
    const map = {}
    for (const o of availableItems.value) map[o.product_id] = o.performer_id ? o.name : null
    return map
})
const performerPhotoById = computed(() => {
    const map = {}
    for (const o of availableItems.value) map[o.product_id] = o.performer_id ? o.photo : null
    return map
})
const offersCount = computed(() => availableItems.value.length)
const shopUrlRef = computed(() => props.shopUrl)
const analyticsContextRef = computed(() => props.analyticsContext)

const {
    balance, isUserLoggedIn, track, joinRedirect, copied, copyShareLink,
    purchasing, purchaseFlash, contribute,
    membershipUpsell, closeMembershipUpsell, trackMembershipUpsellCta,
    successModal, successCopy, statBadges, closeSuccessModal, copyDiscountValue, isUrl,
    interstitialAd, interstitialAdContext, closeInterstitialAd,
    buyStarsModalOpen, openBuyStarsModal, closeBuyStarsModal, handleModalPackSelected, handleModalBuyStarPack,
    tosAccepted, tosShake, handlePackBuyClick,
} = useStarShopPurchase({
    analyticsContext: analyticsContextRef,
    mechanicByProductId,
    productNameById,
    performerNameById,
    performerPhotoById,
    offersCount,
    shopUrl: shopUrlRef,
    location: 'shop_vault_page',
})

function trackItemViewed(productId) {
    track('Star Shop Item Viewed', {}, productId)
}

function trackItemDownloaded(productId) {
    track('Star Shop Item Downloaded', {}, productId)
}

// Staircase visual: packs grow taller left-to-right (matches Index.vue's Star Shop).
function packHeight(i) {
    const n = props.starPacks.length
    const minH = 190, maxH = 460
    return n <= 1 ? maxH : Math.round(minH + (maxH - minH) * (i / (n - 1)))
}

// Unlocked videos get their own row (see below) rather than taking up slots
// in the purchasable category rows — keeps those rows focused on what's
// still buyable instead of burying it behind stuff already owned.
const purchasableItems = computed(() => availableItems.value.filter(o => !o.unlocked))
const unlockedItems = computed(() => availableItems.value.filter(o => o.unlocked))

// One row per category, in the backend's canonical enum order — only
// categories that actually have items get a row.
const itemsByCategory = computed(() => {
    const groups = {}
    for (const item of purchasableItems.value) {
        const key = item.category || 'Uncategorized'
        if (!groups[key]) groups[key] = []
        groups[key].push(item)
    }
    const orderedKeys = [...props.categoryOrder.filter(c => groups[c]), ...Object.keys(groups).filter(c => !props.categoryOrder.includes(c))]
    return orderedKeys.map(category => ({ category, items: groups[category], color: getCategoryColor(category) }))
})

// Category filter chips — "All" plus one per category that actually has items,
// plus an opt-in "Unlocked" chip. Unlocked videos are hidden by default (see
// showUnlockedRow/visibleCategoryGroups below) so the page's first impression
// stays focused on purchasable content, same reasoning as pulling them out of
// the category rows in the first place — one click surfaces them instead.
const selectedCategory = ref('all')
const showUnlockedRow = computed(() => selectedCategory.value === 'unlocked')
const visibleCategoryGroups = computed(() => {
    if (selectedCategory.value === 'unlocked') return []
    return selectedCategory.value === 'all'
        ? itemsByCategory.value
        : itemsByCategory.value.filter(g => g.category === selectedCategory.value)
})

function categoryFilterStyle(color, active) {
    return active
        ? { borderColor: color, color, background: `${color}33` }
        : { '--filter-accent': color }
}

const hasProducts = computed(() => availableItems.value.length > 0)

// Load only CARDS_PER_ROW items per category up front — same "see all in a
// modal" pattern as the main /shop page's other mechanic rows.
const sectionUrl = computed(() => route(page.props.routePrefix + '.shop.section'))
const activeModal = ref(null)
function openCategoryModal(group) {
    activeModal.value = {
        type: 'vault_video',
        category: group.category,
        total: group.items.length,
        fetchUrl: sectionUrl.value,
        eyebrow: group.category,
        title: `All ${group.items.length} ${group.category} video${group.items.length === 1 ? '' : 's'}`,
        accent: group.color,
        pageSize: 8,
        columns: 4,
    }
    track('Star Shop Section Expanded', { section_type: 'vault_video', section_category: group.category, section_total: group.items.length })
}
function closeCategoryModal() {
    activeModal.value = null
}

const UNLOCKED_ACCENT = '#FCD34D'
function openUnlockedModal() {
    activeModal.value = {
        type: 'vault_video',
        unlockedOnly: true,
        total: unlockedItems.value.length,
        fetchUrl: sectionUrl.value,
        eyebrow: 'Your unlocks',
        title: `All ${unlockedItems.value.length} unlocked video${unlockedItems.value.length === 1 ? '' : 's'}`,
        accent: UNLOCKED_ACCENT,
        pageSize: 8,
        columns: 4,
    }
    track('Star Shop Section Expanded', { section_type: 'vault_video', section_unlocked_only: true, section_total: unlockedItems.value.length })
}

onMounted(() => {
    track('Star Shop Page Viewed', {
        star_packs_count: props.starPacks.length,
        highlight_product_id: props.highlightProductId,
    }, props.highlightProductId)

    if (props.highlightProductId) {
        // The highlighted product may be sitting in the "Unlocked" row, which is
        // hidden by default (see selectedCategory/showUnlockedRow) — switch to
        // that filter first so the card actually exists in the DOM to scroll to
        // (e.g. someone clicking "View" from their store-purchases history).
        if (unlockedItems.value.some(o => o.product_id === props.highlightProductId)) {
            selectedCategory.value = 'unlocked'
        }

        nextTick(() => {
            const el = document.querySelector(`[data-product-id="${props.highlightProductId}"]`)
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                el.classList.add('card--highlight')
                setTimeout(() => el.classList.remove('card--highlight'), 2500)
            }
        })
    }
})
</script>

<template>
    <div class="shop" data-screen-label="Star Shop Vault">
        <HeaderV2
            :isUserLoggedIn="isUserLoggedIn"
            :isGoldUser="isGoldUser"
            :requiresAgeVerification="requiresAgeVerification"
            :routePrefix="page.props.routePrefix"
            :shopBadge="true"
            :hide="{ join: true, signIn: true }"
            :promoteFreeJoin="false"
            @buyStarsClick="openBuyStarsModal('star_shop_vault_header')"
            @goldJoinClick="openBuyStarsModal('star_shop_vault_header_gold_cta')"
            @joinClick="joinRedirect('headerJoinButton_starShopVault')"
        />

        <BuyStarsModal
            :isOpen="buyStarsModalOpen"
            :userBalance="balance"
            :starPacks="starPacks"
            @close="closeBuyStarsModal"
            @buy="handleModalBuyStarPack"
            @select="handleModalPackSelected"
        />

        <MembershipUpsellModal
            :isOpen="membershipUpsell.open"
            :membershipStatus="membershipUpsell.status"
            :intent="membershipUpsell.intent"
            :performerName="membershipUpsell.performerName"
            :performerPhoto="membershipUpsell.performerPhoto"
            :productId="membershipUpsell.productId"
            :productTitle="membershipUpsell.productTitle"
            :productKind="membershipUpsell.productKind"
            :productStars="membershipUpsell.productStars"
            :performerId="membershipUpsell.performerId"
            source="star_shop_page"
            @close="closeMembershipUpsell"
            @cta="trackMembershipUpsellCta"
        />

        <InterstitialAdModal
            :isOpen="interstitialAd.open"
            :creative="interstitialAd.creative"
            trigger="post_purchase"
            placement="star_shop_success"
            source="star_shop_vault_page"
            :context="interstitialAdContext"
            @close="closeInterstitialAd"
            @cta="closeInterstitialAd"
        />

        <!-- Decorative side gutter stars — desktop only -->
        <div class="shop__deco shop__deco--left" aria-hidden="true">
            <StarMarkSVG
                v-for="i in 16" :key="i" :size="72"
                class="shop__deco-star"
                :style="{ animationDelay: `${(i - 1) * 0.42}s` }"
            />
        </div>
        <div class="shop__deco shop__deco--right" aria-hidden="true">
            <StarMarkSVG
                v-for="i in 16" :key="i" :size="72"
                class="shop__deco-star"
                :style="{ animationDelay: `${(i - 1) * 0.42 + 0.21}s` }"
            />
        </div>

        <transition name="flash">
            <div v-if="purchaseFlash.message" class="purchase-toast purchase-toast--error">
                {{ purchaseFlash.message }}
            </div>
        </transition>

        <transition name="modal-fade">
            <div v-if="successModal.open" class="success-backdrop" @click.self="closeSuccessModal">
                <div class="success-modal">
                    <CelebrationBurst :active="successModal.open" />
                    <div class="success-modal__icon">
                        <StarMarkSVG :size="28" />
                    </div>

                    <template v-if="successModal.discountItemValue">
                        <div class="success-modal__title">{{ successCopy.title }}</div>
                        <div v-if="statBadges.length" class="success-modal__badges">
                            <div v-for="b in statBadges" :key="b.type" class="stat-badge" :class="{ 'stat-badge--hero': b.hero }">
                                <StarMarkSVG v-if="b.icon === 'brand-star'" :size="12" />
                                <StarIcon v-else :name="b.icon" :size="12" />
                                <span>{{ b.label }}</span>
                            </div>
                        </div>
                        <p class="success-modal__message">Unlocked! Your {{ successCopy.noun }} is below. We've also emailed it to you so you'll always have it handy. You can review all your unlocks in your <a :href="route('members2.account.storePurchases')" class="success-modal__link">purchase history</a>.</p>
                        <div class="eyebrow" :style="{ color: successCopy.accent }" style="margin-bottom:8px;text-align:left">{{ successCopy.eyebrow }}</div>
                        <template v-if="isUrl(successModal.discountItemValue)">
                            <a :href="successModal.discountItemValue" target="_blank" rel="noopener" class="of-reveal__link" :class="`of-reveal__link--${successCopy.theme}`">{{ successModal.discountItemValue }}</a>
                        </template>
                        <template v-else>
                            <div class="of-reveal__code" :class="`of-reveal__code--${successCopy.theme}`">{{ successModal.discountItemValue }}</div>
                        </template>
                        <div style="display:flex;gap:8px;margin-top:20px">
                            <button class="btn" :class="`btn--${successCopy.theme}`" style="flex:1" @click="copyDiscountValue">{{ isUrl(successModal.discountItemValue) ? 'Copy link' : 'Copy code' }}</button>
                            <button class="btn btn--ghost" style="flex:1" @click="closeSuccessModal">Done</button>
                        </div>
                    </template>
                    <template v-else>
                        <div class="success-modal__title">Thank you!</div>
                        <div v-if="statBadges.length" class="success-modal__badges">
                            <div v-for="b in statBadges" :key="b.type" class="stat-badge" :class="{ 'stat-badge--hero': b.hero }">
                                <StarMarkSVG v-if="b.icon === 'brand-star'" :size="12" />
                                <StarIcon v-else :name="b.icon" :size="12" />
                                <span>{{ b.label }}</span>
                            </div>
                        </div>
                        <p class="success-modal__message">Unlocked! We've sent a confirmation to your email — keep an eye on your inbox. You can also view all your unlocks in your <a :href="route('members2.account.storePurchases')" class="success-modal__link">purchase history</a>.</p>
                        <button class="btn btn--gold success-modal__cta" @click="closeSuccessModal">Done</button>
                    </template>
                </div>
            </div>
        </transition>

        <template v-if="hasProducts">
            <section class="hero">
                <div class="hero__grid">
                    <div>
                        <div class="eyebrow eyebrow--pill">
                            <StarIcon name="sparkle" :size="11" :stroke-width="2.5" /> Clips
                        </div>
                        <h1 class="hero__h1">Unlock <span class="grad">clips</span> with stars.</h1>
                        <p class="hero__sub">Behind-the-scenes footage, director's cuts, unreleased clips, and more — spend stars to unlock any clip, yours to keep once claimed.</p>
                        <a :href="route(page.props.routePrefix + '.shop.index')" class="hero__shop-cta">
                            <StarMarkSVG :size="16" /> Explore the Shop <span class="hero__shop-cta-arrow">→</span>
                        </a>
                    </div>

                    <div class="hero__star hero__star--a"><StarMarkSVG :size="36" /></div>
                    <div class="hero__star hero__star--b"><StarMarkSVG :size="20" /></div>
                    <div class="hero__star hero__star--c"><StarMarkSVG :size="14" /></div>
                    <div class="hero__star hero__star--d"><StarMarkSVG :size="28" /></div>
                    <div class="hero__star hero__star--e"><StarMarkSVG :size="10" /></div>
                    <div class="hero__star hero__star--f"><StarMarkSVG :size="18" /></div>
                    <div class="hero__star hero__star--g"><StarMarkSVG :size="8" /></div>

                    <aside v-if="isUserLoggedIn" class="balance-card">
                        <div class="eyebrow eyebrow--mute">Your balance</div>
                        <div class="balance-card__row">
                            <span class="balance-card__star-wrap">
                                <StarMarkSVG :size="32" />
                            </span>
                            <span class="balance-card__num">{{ balance.toLocaleString() }}</span>
                            <span class="balance-card__lbl">stars</span>
                        </div>
                        <div class="balance-card__sub">Stars expire 1 year after purchase</div>
                        <div class="balance-card__actions">
                            <button class="btn btn--blue" @click="openBuyStarsModal('star_shop_vault_balance_card')">Buy more stars</button>
                            <a :href="route('members2.account.storePurchases')" class="btn btn--ghost">History</a>
                        </div>
                    </aside>

                    <aside v-else class="balance-card balance-card--guest">
                        <div class="balance-card__guest-icon">
                            <StarMarkSVG :size="36" />
                        </div>
                        <div class="balance-card__guest-h">Unlock the clips.</div>
                        <p class="balance-card__guest-sub">Join to get stars with your membership and unlock any clip.</p>
                        <div class="balance-card__actions">
                            <a :href="route('www.join.signUp', {custom6: 'starShopVaultJoin'})" class="btn btn--blue">Join now</a>
                            <a :href="route('members2.auth.login')" class="btn btn--ghost">Sign in</a>
                        </div>
                    </aside>
                </div>
            </section>

            <section class="catalog-head">
                <div class="eyebrow eyebrow--center" style="color:#FCD34D">The clips</div>
                <h2 class="display">{{ itemsByCategory.length }} categor{{ itemsByCategory.length === 1 ? 'y' : 'ies' }} to unlock right now.</h2>
                <p class="catalog-head__sub">New clips are added regularly — check back for more.</p>
            </section>

            <div v-if="itemsByCategory.length > 1 || unlockedItems.length" class="category-filters">
                <button
                    class="category-filter"
                    :class="{ 'category-filter--active': selectedCategory === 'all' }"
                    :style="categoryFilterStyle('#FCD34D', selectedCategory === 'all')"
                    @click="selectedCategory = 'all'"
                >All categories</button>
                <button
                    v-for="group in itemsByCategory" :key="group.category"
                    class="category-filter"
                    :class="{ 'category-filter--active': selectedCategory === group.category }"
                    :style="categoryFilterStyle(group.color, selectedCategory === group.category)"
                    @click="selectedCategory = group.category"
                >{{ group.category }}</button>
                <button
                    v-if="unlockedItems.length"
                    class="category-filter"
                    :class="{ 'category-filter--active': showUnlockedRow }"
                    :style="categoryFilterStyle(UNLOCKED_ACCENT, showUnlockedRow)"
                    @click="selectedCategory = 'unlocked'"
                >Unlocked ({{ unlockedItems.length }})</button>
            </div>

            <div class="rows" style="gap: 24px">
                <RowShell
                    v-if="showUnlockedRow"
                    index="✓" accent="#FCD34D" eyebrow="Your unlocks"
                    :title="`${unlockedItems.length} unlocked clip${unlockedItems.length === 1 ? '' : 's'}.`"
                >
                    <div class="grid grid--4">
                        <DirectAccessProductCard
                            v-for="o in unlockedItems.slice(0, CARDS_PER_ROW)" :key="o.id"
                            :item="o" :purchasing="purchasing" :is-copied="copied === o.product_id"
                            :data-product-id="o.product_id"
                            @contribute="contribute(o.product_id, o.price, o.performer_id)"
                            @share="copyShareLink(o.product_id)"
                            @viewed="trackItemViewed"
                            @download="trackItemDownloaded"
                        />
                    </div>

                    <template v-if="unlockedItems.length > CARDS_PER_ROW" #footer-action>
                        <button class="row-see-all__link" :style="{ color: UNLOCKED_ACCENT }" @click="openUnlockedModal">
                            See all {{ unlockedItems.length }} unlocked videos
                        </button>
                    </template>
                </RowShell>

                <RowShell
                    v-for="(group, i) in visibleCategoryGroups" :key="group.category"
                    :index="String(i + 1).padStart(2, '0')" :accent="group.color" :eyebrow="group.category"
                    :title="`${group.items.length} clip${group.items.length === 1 ? '' : 's'} to unlock.`"
                >
                    <div class="grid grid--4">
                        <DirectAccessProductCard
                            v-for="o in group.items.slice(0, CARDS_PER_ROW)" :key="o.id"
                            :item="o" :purchasing="purchasing" :is-copied="copied === o.product_id"
                            :data-product-id="o.product_id"
                            @contribute="contribute(o.product_id, o.price, o.performer_id)"
                            @share="copyShareLink(o.product_id)"
                            @viewed="trackItemViewed"
                            @download="trackItemDownloaded"
                        />
                    </div>

                    <template v-if="group.items.length > CARDS_PER_ROW" #footer-action>
                        <button class="row-see-all__link" :style="{ color: group.color }" @click="openCategoryModal(group)">
                            See all {{ group.items.length }} {{ group.category }} videos
                        </button>
                    </template>
                </RowShell>
            </div>

            <ShopAllModal
                v-if="activeModal" :open="true" @update:open="closeCategoryModal"
                :type="activeModal.type" :total="activeModal.total"
                :fetch-url="activeModal.fetchUrl"
                :extra-params="activeModal.unlockedOnly ? { unlocked_only: 1 } : { category: activeModal.category }"
                :eyebrow="activeModal.eyebrow" :title="activeModal.title"
                :accent="activeModal.accent" :page-size="activeModal.pageSize"
                :columns="activeModal.columns"
            >
                <template #default="{ visibleItems }">
                    <div class="grid grid--4">
                        <DirectAccessProductCard
                            v-for="o in visibleItems" :key="o.id"
                            :item="o" :purchasing="purchasing" :is-copied="copied === o.product_id"
                            @contribute="contribute(o.product_id, o.price, o.performer_id)"
                            @share="copyShareLink(o.product_id)"
                            @viewed="trackItemViewed"
                            @download="trackItemDownloaded"
                        />
                    </div>
                </template>
            </ShopAllModal>

            <!-- ────────── STAR PACKS STAIRCASE ────────── -->
            <section v-if="starPacks.length" id="buy-stars" class="staircase">
                <div class="staircase__head">
                    <div class="eyebrow eyebrow--center" style="color:#FCD34D">Top up</div>
                    <h2 class="display">Power up.</h2>
                    <p class="catalog-head__sub">More stars, more clips. Stars expire 1 year after purchase.</p>
                </div>
                <div class="staircase__grid">
                    <article v-for="(p, i) in starPacks" :key="i" class="pack"
                             :class="[{ 'pack--popular': p.popular, 'pack--best': p.best }]"
                             :style="{ height: packHeight(i) + 'px' }"
                             @click="handlePackBuyClick(p)">
                        <span v-if="p.popular" class="pack__pill pack__pill--blue">Most popular</span>
                        <span v-if="p.best" class="pack__pill pack__pill--gold">Best value</span>
                        <div>
                            <div class="eyebrow muted">{{ p.label }}</div>
                            <div class="pack__count">
                                <StarMarkSVG :size="Math.min(18 + i * 4, 32)" />
                                <span class="pack__num pack__num--gold" :style="{ fontSize: (22 + i * 4) + 'px' }">{{ p.stars.toLocaleString() }}</span>
                            </div>
                        </div>
                        <div>
                            <div class="pack__price pack__price--muted" :style="{ fontSize: (20 + i * 2) + 'px' }">{{ '$' + p.price.toFixed(2).replace(/\.00$/, '') }}</div>
                            <button
                               class="btn w-full"
                               :class="[p.best ? 'btn--gold' : p.popular ? 'btn--blue' : 'btn--gold-outline', { 'btn--tos-locked': !tosAccepted }]"
                               style="margin-top:12px"
                               >Buy</button>
                        </div>
                    </article>
                </div>

                <div class="staircase__tos" :class="{ 'staircase__tos--shake': tosShake }">
                    <label class="staircase__tos-label">
                        <input type="checkbox" v-model="tosAccepted" class="staircase__tos-check" />
                        <span>
                            I agree to the
                            <a href="https://www.naughtyamerica.com/terms-of-service.html" target="_blank" rel="noopener" class="staircase__tos-link">Terms of Service</a>
                            — Stars expire 1 year after purchase.
                        </span>
                    </label>
                </div>
            </section>
        </template>

        <template v-else>
            <section class="hero">
                <div class="hero__grid" style="grid-template-columns: 1fr;">
                    <div>
                        <div class="eyebrow eyebrow--pill">
                            <StarIcon name="sparkle" :size="11" :stroke-width="2.5" /> Clips
                        </div>
                        <h1 class="hero__h1">The clips are <span class="grad">on the way</span>.</h1>
                        <p class="hero__sub">Check back soon — new clips are added regularly.</p>
                        <a :href="route(page.props.routePrefix + '.shop.index')" class="hero__shop-cta">
                            <StarMarkSVG :size="16" /> Explore the Shop <span class="hero__shop-cta-arrow">→</span>
                        </a>
                    </div>
                </div>
            </section>
        </template>
    </div>
</template>

<style scoped>
@import './shop-shared.css';

/* ─── Hero "Explore the Shop" CTA ─── */
.hero__shop-cta {
    display: inline-flex; align-items: center; gap: 8px;
    margin-top: 20px; padding: 13px 22px;
    background: linear-gradient(135deg,#FB923C,#EA580C);
    color: #3a1a02; font-weight: 800; font-size: 15px;
    border-radius: 10px; text-decoration: none;
    box-shadow: 0 10px 28px rgba(251,146,60,0.30);
    transition: transform 120ms ease, box-shadow 120ms ease;
}
.hero__shop-cta:hover { transform: translateY(-1px); box-shadow: 0 14px 32px rgba(251,146,60,0.40); }
.hero__shop-cta-arrow { transition: transform 120ms ease; }
.hero__shop-cta:hover .hero__shop-cta-arrow { transform: translateX(3px); }

/* ─── See all button (not in shop-shared.css — Index.vue defines its own
     copy scoped to itself, so Vault.vue needs its own too) ─── */
.row-see-all__link {
    background: none; border: none; cursor: pointer;
    font-size: 12px; font-weight: 600;
    letter-spacing: 0.03em;
    padding: 0;
    transition: opacity 0.15s;
}
.row-see-all__link:hover { opacity: 0.75; }

/* ─── Category filter chips ─── */
.category-filters {
    display: flex; flex-wrap: wrap; gap: 8px;
    justify-content: center;
    margin: 0 auto 28px;
    max-width: 960px;
}
.category-filter {
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.14);
    color: #eaeaea;
    font-size: 12px; font-weight: 700;
    letter-spacing: 0.02em;
    padding: 8px 16px;
    border-radius: 9999px;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s, color 0.15s;
}
.category-filter:hover { border-color: var(--filter-accent); color: #fff; }
</style>
